import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, Sparkles, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Mega-menu for the global navbar.
 * Hover-aware (with 200ms grace period to bridge gap between trigger and panel).
 * Loads top-level categories and their first-level children once at mount.
 */
export const MegaMenu = () => {
  const { language, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [topCats, setTopCats] = useState([]);
  const [allCats, setAllCats] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const all = await axios.get(`${API}/categories`);
        setAllCats(all.data);
        setTopCats(all.data.filter((c) => !c.parent_id));
      } catch (_) {
        /* silent */
      }
    };
    fetch();
  }, []);

  // a11y: Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const T = (fr, en) => (language === 'fr' ? fr : en);

  const subsOf = (parentId) => allCats.filter((c) => c.parent_id === parentId);
  const localizedName = (c) => (language === 'fr' ? c.name_fr : c.name_en) || c.name;

  const onEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const onLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };

  const hoveredCat = topCats.find((c) => c.id === hoveredId) || topCats[0];
  const hoveredSubs = hoveredCat ? subsOf(hoveredCat.id) : [];

  if (topCats.length === 0) return null;

  return (
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave} data-testid="mega-menu">
      <button
        type="button"
        className={`flex items-center gap-1.5 text-sm font-semibold transition-colors ${
          open ? 'text-[#0F4C5C]' : 'text-[#0F4C5C]/80 hover:text-[#0F4C5C]'
        }`}
        onFocus={onEnter}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="mega-menu-trigger"
      >
        <Sparkles className="w-4 h-4 text-[#E9C46A]" />
        <span>{t('navAllCategories')}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full pt-3 w-screen max-w-[1100px] z-50"
          style={{ left: 'calc(-1 * (var(--mega-offset, 0px)))' }}
        >
          <div className="bg-white rounded-2xl shadow-2xl shadow-[#0F4C5C]/15 border border-[#0F4C5C]/10 overflow-hidden">
            <div className="grid grid-cols-[280px_1fr]">
              {/* Left: top-level list */}
              <div className="bg-[#FBF8F3] py-3 max-h-[480px] overflow-y-auto">
                {topCats.map((c) => {
                  const active = (hoveredId || topCats[0]?.id) === c.id;
                  return (
                    <Link
                      key={c.id}
                      to={`/shop?category=${c.slug}`}
                      onMouseEnter={() => setHoveredId(c.id)}
                      onClick={() => setOpen(false)}
                      className={`flex items-center justify-between px-5 py-2.5 text-sm transition-colors group ${
                        active
                          ? 'bg-[#0F4C5C] text-white font-semibold'
                          : 'text-[#0F4C5C]/80 hover:text-[#0F4C5C] hover:bg-white'
                      }`}
                      data-testid={`mega-cat-${c.slug}`}
                    >
                      <span className="truncate">{localizedName(c)}</span>
                      <ArrowRight
                        className={`w-3.5 h-3.5 ml-2 flex-shrink-0 transition-opacity ${
                          active ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
                        }`}
                      />
                    </Link>
                  );
                })}
                <Link
                  to="/categories"
                  onClick={() => setOpen(false)}
                  className="block mx-5 mt-3 text-xs uppercase tracking-widest font-semibold text-[#E9C46A] hover:text-[#0F4C5C] transition-colors"
                >
                  {T('Voir toutes les catégories →', 'View all categories →')}
                </Link>
              </div>

              {/* Right: hovered category preview */}
              <div className="p-7 grid grid-cols-2 gap-x-8 gap-y-2 relative">
                {hoveredCat && (
                  <>
                    <div className="col-span-2 flex items-end justify-between mb-2">
                      <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-[#E9C46A] font-semibold">
                          {T('Explorer', 'Explore')}
                        </p>
                        <h3 className="text-2xl font-bold text-[#0F4C5C] mt-1 tracking-tight">
                          {localizedName(hoveredCat)}
                        </h3>
                      </div>
                      <Link
                        to={`/shop?category=${hoveredCat.slug}`}
                        onClick={() => setOpen(false)}
                        className="text-sm font-semibold text-[#0F4C5C] hover:text-[#E9C46A] flex items-center gap-1.5"
                      >
                        {T('Tout parcourir', 'Browse all')} <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                    {hoveredSubs.length > 0 ? (
                      hoveredSubs.map((sub) => (
                        <Link
                          key={sub.id}
                          to={`/shop?category=${sub.slug}`}
                          onClick={() => setOpen(false)}
                          className="text-sm text-[#0F4C5C]/70 hover:text-[#0F4C5C] py-1.5 hover:translate-x-0.5 transition-all"
                        >
                          {localizedName(sub)}
                        </Link>
                      ))
                    ) : (
                      <div className="col-span-2 text-sm text-[#0F4C5C]/60 italic py-4">
                        {T(
                          'Cette catégorie regroupe directement tous ses produits — cliquez pour les découvrir.',
                          'This category groups all its products directly — click to discover them.'
                        )}
                      </div>
                    )}
                    {hoveredCat.image_url && (
                      <div className="col-span-2 mt-4 rounded-xl overflow-hidden h-28 bg-[#FBF8F3]">
                        <img
                          src={hoveredCat.image_url}
                          alt={localizedName(hoveredCat)}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
